import React, { FC, useState, useContext, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Typography from "@mui/material/Typography";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem'

// MUI Icons
import PlaceIcon from "@mui/icons-material/Place";
import DownloadIcon from '@mui/icons-material/Download';

// Ol
import Geometry from 'ol/geom/Geometry';
import OlFeature from 'ol/Feature';
import Point from 'ol/geom/Point';

// Custom
import mapService from '@/services/mapService';
import { GridItem } from '@/ui/Grid';
import useMapPrint, { PaperFormat, PaperOrientation, FileFormat, printScales, PrintScale } from '@/components/useMapPrint';


// Types
import { IApiResponse } from '@/@types/lib/api';
import SnackbarContext from "@/context/SnackbarContext/SnackbarContext";
import MapContext, { MapContextType } from "@/context/MapContext/MapContext";
import PrintOverlay from '@/ui/Dialog/PrintOverlay';

type PrintPaneProps = {
  mapId: number;
}

const PrintPane: FC<PrintPaneProps> = ({ mapId }) => {
  const { paperFormat, setPaperFormat, paperOrientation, setPaperOrientation, fileFormat, setFileFormat, frameDimensions, printScale, setPrintScaleManually, downloadMap } = useMapPrint({ mapId });
  const { showNotification } = useContext(SnackbarContext)
  const mapContext = useContext(MapContext)
  const { map } = mapContext as MapContextType;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // const [scaleValue, setScaleValue] = useState<string>("10000");

  const [value, setValue] = useState<string>('');
  const [showPrintOverlay, setShowPrintOverlay] = useState(true);

  /* useEffect(() => {
    if (printScale > 100) {
      const s = Math.round(printScale / 100) * 100;
      setScaleValue(s.toString());
    }
    else {
      setScaleValue(Math.round(printScale).toString());
    }
  }, [printScale]); */


  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const val = evt.target.value.toUpperCase();
    setValue(val);
  };

  const handleShowMap = async () => {
    const resp: IApiResponse = await mapService.loadPrintConfig(value.toLowerCase());

    if (resp.success && resp.data) {
      // @ts-ignore
      const {config} = resp.data;
      navigate(`/map#print?hash=${value.toLowerCase()}`, { state: { config } });
      if (map) {
        const view = map.getView();
        // @ts-ignore
        const data = JSON.parse(config);
        const viewsStateObj = JSON.parse(data.viewsState);
        const firstView = viewsStateObj.views?.[0];
        const pointFeature: OlFeature<Geometry> = new OlFeature({
          geometry: new Point(firstView.center)
        });
        const extent = pointFeature.getGeometry()?.getExtent();
        if (extent)
          view.fit(extent, {
            maxZoom: firstView.zoom
          });
      }
      return;
    }
    showNotification(t('map:sidebar.qrcode_error'), "error");
  };

  const handlePaperFormatClick = (format: PaperFormat) => {
    setShowPrintOverlay(true);
    setPaperFormat(format);
  }

  const handlePaperOrientationClick = (orientation: PaperOrientation) => {
    setShowPrintOverlay(true);
    setPaperOrientation(orientation);
  }


  const handleFileFormatClick = (format: FileFormat) => {
    setShowPrintOverlay(true);
    setFileFormat(format);
  }

  const handlePrintMap = () => {
    downloadMap();
  }

  const handleScalePickerChange = useCallback((event: SelectChangeEvent<number | ''>) => {
    setShowPrintOverlay(true);
    setPrintScaleManually(event.target.value as PrintScale)
  }, []);

  // const handleScaleChange: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
  //   const inputValue = evt.target.value;

  //   console.log(inputValue);

  //   setScaleValue(inputValue);
  // }

  // const handleScaleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (evt: React.KeyboardEvent<HTMLInputElement>) => {
  //   if (evt.key === 'Enter') {
  //     const s = parseInt(scaleValue, 10);
  //     setPrintScaleManually(s);
  //   }
  // }

  return (
    <Box>
      <Typography sx={{ mb: 1 }} variant="h5">
        {t('map:titles.print_print')}
      </Typography>

      <Typography sx={{ mb: 1 }} variant="body2">
        {t('map:map_print_desc')}
      </Typography>
      {/* <Typography sx={{ mb: 1 }}>
        {t('map:sidebar.print')}:
      </Typography> */}
      <FormLabel sx={{ fontSize: "14px" }}>
        {t("map:map_printlabel_size")}
      </FormLabel>
      <ButtonGroup variant="outlined" size="small" sx={{ width: "100%", mb: 1 }}>
        <Button
          variant={paperFormat === "A4" ? "contained" : "outlined"}
          onClick={() => handlePaperFormatClick("A4")}
          sx={{ flex: 1 }}
        >
          A4
        </Button>
        <Button
          variant={paperFormat === "A3" ? "contained" : "outlined"}
          onClick={() => handlePaperFormatClick("A3")}
          sx={{ flex: 1 }}
        >
          A3
        </Button>
      </ButtonGroup>
      <FormLabel sx={{ fontSize: "14px" }}>
        {t("map:map_printlabel_orientation")}
      </FormLabel>
      <ButtonGroup variant="outlined" size="small" sx={{ width: "100%", mb: 1 }}>
        <Button
          variant={paperOrientation === "portrait" ? "contained" : "outlined"}
          onClick={() => handlePaperOrientationClick("portrait")}
          sx={{ flex: 1 }}
        >
          {t("map:map_print_portret")}
        </Button>
        <Button
          variant={paperOrientation === "landscape" ? "contained" : "outlined"}
          onClick={() => handlePaperOrientationClick("landscape")}
          sx={{ flex: 1 }}
        >
          {t("map:map_print_landscape")}
        </Button>
      </ButtonGroup>

      <FormLabel sx={{ fontSize: "14px" }}>
        {t("map:map_printlabel_fileformat")}
      </FormLabel>
      <ButtonGroup variant="outlined" size="small" sx={{ width: "100%", mb: 1 }}>
        <Button
          variant={fileFormat === "pdf" ? "contained" : "outlined"}
          onClick={() => handleFileFormatClick("pdf")}
          sx={{ flex: 1 }}
        >
          {t("map:map_print_document")}
        </Button>
        <Button
          variant={fileFormat === "png" ? "contained" : "outlined"}
          onClick={() => handleFileFormatClick("png")}
          sx={{ flex: 1 }}
        >
          {t("map:map_print_image")}
        </Button>
      </ButtonGroup>

      <FormLabel sx={{ fontSize: "14px" }}>
        {t("map:map_printlabel_printscale")}
      </FormLabel>
      <FormControl fullWidth>
        <Select
          size="small"
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          value={printScale}
          onChange={handleScalePickerChange}
        >
          {printScales.map(x => <MenuItem value={x}>{`1:${x}`}</MenuItem>)}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        size="small"
        onClick={handlePrintMap}
        startIcon={<DownloadIcon />}
        sx={{ width: '100%', my: 2 }}
      >
        {t("buttons.export")}
      </Button>

      <Divider sx={{ my: 2 }} />

      <Typography sx={{ mb: 1 }} variant="h5">
        {t('map:titles.print_redigitalisation')}
      </Typography>

      <Typography sx={{ mb: 1 }} variant="body2">
        {t('map:map_redigitalise_desc')}
        {/* {t('map:sidebar.qrcode_hash')}: */}
      </Typography>
      <TextField
        size="small"
        sx={{ mb: 1 }}
        fullWidth
        placeholder='A8GW6F'
        value={value}
        onChange={handleChange}
        slotProps={
          {
            htmlInput: { maxLength: 6, pattern: "[ABCDEFGHIJKLMNPQRSTUVWXabcdefghijklmnpqrstuvwx1-9]*" }
          }
        }
      />
      <GridItem size={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleShowMap}
          startIcon={<PlaceIcon />}
          sx={{ width: '100%' }}
          disabled={value.length < 6}
        >
          {t('map:sidebar.qrcode_button')}
        </Button>
      </GridItem>

      {showPrintOverlay && mapContext?.map && (
        <PrintOverlay
          frameDimensions={frameDimensions}
          onClose={() => setShowPrintOverlay(false)}
        />
      )}
    </Box>
  );
};

export default PrintPane;
